import React, {lazy, Suspense, useEffect, useMemo, useState} from 'react';
import {isFunction} from '@assets/helpers/isFunction';
import {
  getWidgetSettingsStorage,
  setWidgetSettingsStorage
} from '@scripttag/helpers/sessionHelpers';
import useHashAction from '@assets/hooks/widget/useHashAction';
import {prepareDefaultColors} from '@assets/helpers/widget/themeHelpers';
import {prepareFont} from '@assets/config/fonts/fonts';
import {checkTimeAvailability} from '@functions/helpers/datetime/prepareTime';
import useGoogleFonts from '@scripttag/widgets/hooks/useGoogleFonts';
import useDelayLoad from '@assets/hooks/useDelayLoad';
import {closeJoyWidget} from '@assets/helpers/widget/integrationJoyWidget';
import {generateUUID} from '@assets/helpers/genarateUUID';
import {checkIsChatBot} from '@assets/helpers/checkIsChatBot';
import updateRootStyles from '@assets/helpers/utils/updateRootStyles';

// console.log('Build target:', process.env.BUILD_TARGET);

// Conditionally import components based on build environment
// This ensures only the needed component is included in each bundle
const PreviewWidget =
  process.env.BUILD_TARGET !== 'scripttag' &&
  lazy(() => import('@assets/components/Preview/PreviewWidget'));
const LiveWidget =
  process.env.BUILD_TARGET !== 'admin' &&
  lazy(() => import('@assets/components/Preview/LiveWidget'));

const storeEmail = window.activeShop?.email || '';

/**
 *
 * @param props
 * @returns {Element}
 * @constructor
 */
function ParentWidget(props) {
  const {
    campaigns,
    isPreview = true,
    translations = {},
    translation = translations[translations.primaryLanguage],
    previewPage,
    settings,
    isFromCampaignPreview = false,
    previewFormContactForm,
    setPreviewFormContactForm
  } = props;

  const [
    isWelcomePopup,
    isNewsletterPopup,
    isChatAvail,
    isProductRecommendationPopup,
    isCartBoosterPopup,
    isBrowserCollectionPagePopup,
    isPreviewAI
  ] = [
    'welcome-popup',
    'newsletter-popup',
    'chat-availability',
    'product-recommendation-popup',
    'cart-booster-popup',
    'browser-collection-page-popup',
    'ai-assistant'
  ].map(x => x === previewPage);

  const widgetPageSettings = getWidgetSettingsStorage();

  const [open, setOpenOrg] = useState(() => {
    if (isPreview)
      return (
        !isWelcomePopup &&
        !isNewsletterPopup &&
        !isProductRecommendationPopup &&
        !isCartBoosterPopup &&
        !isBrowserCollectionPagePopup
      );
    return !!widgetPageSettings.open;
  });

  const setOpen = val => {
    const result = isFunction(val) ? val(open) : val;
    setWidgetSettingsStorage({open: result});
    if (!result && hashUrl) {
      setHashUrl('');
      try {
        window.history.replaceState(null, '', window.location.pathname);
      } catch (e) {
        console.error(e);
      }
    }
    setOpenOrg(val);
  };
  const [isFirstOpen, setIsFirstOpen] = useState(open);
  const [isStaffOnline, setIsStaffOnline] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [isShowDesktop, setIsShowDesktop] = useState(false); // todo rename to isPreviewDesktop
  const [openWelcomePopup, setOpenWelcomePopup] = useState(false);
  const [openNewsletterPopup, setOpenNewsletterPopup] = useState(false);
  const [chatInstance, setChatInstance] = useState(null);
  const [initAuth, setInitAuth] = useState(Boolean(isPreview));
  const {hasInteracted, setHasInteracted} = useDelayLoad({openWidget: open});
  const {hashUrl, setHashUrl} = useHashAction({
    isPreview,
    onHashChange: () => setHasInteracted(true)
  });
  const [activeCampaigns, setActiveCampaigns] = useState([]);

  useGoogleFonts([
    ...new Set([
      settings.headingTextFont,
      settings.contentTextFont,
      settings.welcomePopupSetting.titleTextFont,
      settings.welcomePopupSetting.messageTextFont
    ])
  ]);

  useEffect(() => {
    if (open) closeJoyWidget();
    if (open && !isFirstOpen) setIsFirstOpen(true);
  }, [open]);

  useEffect(() => {
    const {contentTextFont} = prepareDefaultColors(settings);
    updateRootStyles({'--faqsContentTextFont': prepareFont(contentTextFont)});
    // document.documentElement.style.setProperty(
    //   '--faqsContentTextFont',
    //   prepareFont(contentTextFont)
    // );
  }, []);

  const openViaInlineBtn = Boolean(document.querySelector('#openViaInlineButton'));

  const isAvailableTime = useMemo(() => {
    if (isChatAvail) return selectedTab === 0;
    if (isStaffOnline) return true;
    return checkTimeAvailability(settings);
  }, [settings, isChatAvail, selectedTab, isStaffOnline]);

  const isEnableAIAssistant = useMemo(
    () => checkIsChatBot({isAvailableTime, isPreviewAI, settings}),
    [isAvailableTime, isPreviewAI]
  );

  const shouldOpenChat = settings.isEnableChatFocus && !widgetPageSettings.currentPage;

  const sharedProps = {
    setOpenModalWidget: () => {},
    ...props,
    settings: {...settings, isEnableAIAssistant},
    storeEmail,
    isPreview,
    translations,
    translation,
    open,
    setOpen,
    selectedTab,
    setSelectedTab,
    isShowDesktop,
    setIsShowDesktop,
    openWelcomePopup,
    setOpenWelcomePopup,
    openNewsletterPopup,
    setOpenNewsletterPopup,
    chatInstance,
    setChatInstance,
    initAuth,
    setInitAuth,
    isAvailableTime,
    setIsStaffOnline,
    ...((openViaInlineBtn || shouldOpenChat) && settings.isLiveChat
      ? {defaultPage: 'chatDetail', defaultHistory: ['homePage', 'chatDetail'], defaultTab: 1}
      : {}),
    previewFormContactForm,
    setPreviewFormContactForm,
    activeCampaigns: isPreview ? campaigns || [] : activeCampaigns,
    setActiveCampaigns,
    isFromCampaignPreview
  };

  return (
    <Suspense fallback={<div />}>
      {isPreview && PreviewWidget ? (
        <PreviewWidget {...sharedProps} />
      ) : LiveWidget ? (
        <LiveWidget {...{...sharedProps, hasInteracted, isFirstOpen}} />
      ) : (
        <div />
      )}
    </Suspense>
  );
}

export default React.memo(ParentWidget);
